import { TenderNote } from '../tenders/tender.models';
import { HvtSection1Dto, HvtSection2Dto, HvtSection4Dto, HvtSection5Dto, HvtSection6Dto } from '../notices/ojeu.notice.models';
import { FormType } from '../notices/ojeu.notice.enums';
import { NoticeStatus } from '../notices/notice.enums';
import { ContractAwardType } from './contract.award.enums';
import { PagingRequest } from '../common/paging';
import { Configuration } from '../../configuration';
import { Address } from '../common/address';
import {DateRangeDto} from '../common/date.range';

export class ContractAwardSearchModel extends PagingRequest {
    status: NoticeStatus;
    types: Array<ContractAwardType>;
    publishedDateFrom: string;
    publishedDateTo: string;
    contractStartDateFrom: string;
    contractStartDateTo: string;
    contractEndDateFrom: string;
    contractEndDateTo: string;
    keywords: string[];
    excludeKeywords: string[];
    id?: number;
    ojeuId: string;
    awardedCompanyName: string;
    awardedCompanyEmail: string;
    locationIds: Array<number>;
    categoryIds: Array<number>;
    buyerOrganisationName: string;
    buyerContactName: string;
    buyerEmailAddress: string;
    supplierId: number;

    constructor(configuration: Configuration) {
        super(configuration);
        this.types = [ContractAwardType.ContractAward, ContractAwardType.ContractRegistry];
        this.status = NoticeStatus.Live;
        this.categoryIds = [];
        this.locationIds = [];
        this.keywords = [];
        this.excludeKeywords = [];
    }

    setValues(storage: ContractAwardSearchStorage) {
        this.status = storage.status;
        this.types = storage.types;
        this.keywords = storage.keywords;
        this.excludeKeywords = storage.excludeKeywords;
        this.id = storage.id;
        this.ojeuId = storage.ojeuId;
        this.awardedCompanyName = storage.awardedCompanyName;
        this.awardedCompanyEmail = storage.awardedCompanyEmail;
        this.locationIds = storage.locationIds;
        this.categoryIds = storage.categoryIds;
        this.buyerOrganisationName = storage.buyerOrganisationName;
        this.buyerContactName = storage.buyerContactName;
        this.buyerEmailAddress = storage.buyerEmailAddress;
    }

    reset() {
        this.status = NoticeStatus.Live;
        this.types = [ContractAwardType.ContractAward, ContractAwardType.ContractRegistry];
        this.publishedDateFrom = null;
        this.publishedDateTo = null;
        this.contractStartDateFrom = null;
        this.contractStartDateTo = null;
        this.contractEndDateFrom = null;
        this.contractEndDateTo = null;
        this.keywords = [];
        this.excludeKeywords = [];
        this.id = null;
        this.ojeuId = null;
        this.awardedCompanyName = null;
        this.awardedCompanyEmail = null;
        this.locationIds = [];
        this.categoryIds = [];
        this.buyerOrganisationName = null;
        this.buyerContactName = null;
        this.buyerEmailAddress = null;
    }
}

export class ContractAwardSearchStorage {
    types: Array<ContractAwardType>;
    categoryIds: number[];
    locationIds: number[];
    status: NoticeStatus;
    id?: number;
    awardedCompanyName: string;
    awardedCompanyEmail: string;
    ojeuId: string;
    keywords: string[];
    excludeKeywords: string[];
    publishedDate: DateRangeDto;
    contractStartDate: DateRangeDto;
    contractEndDate: DateRangeDto;
    buyerOrganisationName: string;
    buyerContactName: string;
    buyerEmailAddress: string;

    constructor(request: ContractAwardSearchModel) {
        this.types = request.types;
        this.categoryIds = request.categoryIds;
        this.locationIds = request.locationIds;
        this.status = request.status;
        this.id = request.id;
        this.awardedCompanyName = request.awardedCompanyName;
        this.awardedCompanyEmail = request.awardedCompanyEmail;
        this.ojeuId = request.ojeuId;
        this.keywords = request.keywords;
        this.excludeKeywords = request.excludeKeywords;
        this.buyerOrganisationName = request.buyerOrganisationName;
        this.buyerContactName = request.buyerContactName;
        this.buyerEmailAddress = request.buyerEmailAddress;
    }
}

export class ContractAwardSummaryDto {
    title: string;
    b2BRef: number;
    type: string;
    location: string;
    description: string;
    jsLastViewed: string;
    bookmarked: boolean;
    bookmarkedBy: string;
    bookmarkedById: number;
    jsBookmarkedDate: string;
}

export class SearchBookmarkedContractAwardsDto extends PagingRequest {
    status: NoticeStatus = NoticeStatus.Live;
    teamMember: number = 0;
    publishedDateFrom: string;
    publishedDateTo: string;

    equals(other: SearchBookmarkedContractAwardsDto): boolean {
        return this.status === other.status &&
            this.teamMember === other.teamMember &&
            this.publishedDateFrom === other.publishedDateFrom &&
            this.publishedDateTo === other.publishedDateTo &&
            super.equals(other);
    }
}

export class BookmarkedContractAwardSummaryDto extends ContractAwardSummaryDto {
    bookmarkId: number;
}

export class ContractAwardDetailsDto {
    id: number;
    officialTitle: string;
    b2BTitle: string;
    value: string;
    officialContractNumber: string;
    daysToDeadline: number;
    type: string;
    jsInterestDeadline: string;
    jsDocumentDeadline: string;
    jsClarificationDeadline: string;
    jsContractStartDate: string;
    jsContractEndDate: string;
    buyerOrganisationName: string;
    contactName: string;
    contactEmail: string;
    contactNumber: string;
    contactAddress: Address;
    url: string;
    secureUrl: string;
    description: string;
    jsPublishedDate: string;
    placeholder: string;
    ojeuFormType: FormType;
    ojeuSections: Array<number>;
    isSchemaEight: boolean;
    notes: Array<TenderNote>;
    noticeNote: string;

    hvtSection1: HvtSection1Dto;
    hvtSection2: HvtSection2Dto;
    hvtSection4: HvtSection4Dto;
    hvtSection5: HvtSection5Dto;
    hvtSection6: HvtSection6Dto;

    schema8Section1: string;
    schema8Section2: string;
    schema8Section3: string;
    schema8Section4: string;
    schema8Section5: string;
    schema8Section6: string;
    schema8Section7: string;
    schema8Section8: string;
    schema8Section9: string;

    awardDetails: AwardDetailsDto[];

    jsLastViewed: string;
    bookmarked: boolean;
    bookmarkedBy: string;
    bookmarkedById: number;
    jsBookmarkedDate: string;

    constructor(notSubscribed?: boolean) {
        if (notSubscribed) {
            this.placeholder = 'xxxxxxxxxx';

            this.type = this.placeholder;
            this.b2BTitle = this.placeholder;
            this.value = this.placeholder;
            this.officialContractNumber = this.placeholder;
            this.jsInterestDeadline = this.placeholder;
            this.jsDocumentDeadline = this.placeholder;
            this.jsClarificationDeadline = this.placeholder;
            this.jsContractStartDate = this.placeholder;
            this.jsContractEndDate = this.placeholder;
            this.buyerOrganisationName = this.placeholder;
            this.contactName = this.placeholder;
            this.contactEmail = this.placeholder;
            this.contactNumber = this.placeholder;
            this.contactAddress = new Address();
            this.contactAddress.line1 = this.placeholder;
            this.contactAddress.line2 = this.placeholder;
            this.contactAddress.city = this.placeholder;
            this.contactAddress.postcode = this.placeholder;
            this.url = this.placeholder;
            this.secureUrl = this.placeholder;
            this.description = this.placeholder;
        }
    }
}

export class AwardDetailsDto {
    supplierName: string;
    contact: string;
    address: string;
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/models/contract.awards/contract.award.models.ts